import { useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { VideoNotificationDto } from '../constants';
import {
  FOCUS_THROTTLE_MS,
  RECALL_LEASE_MS,
  RECALL_MAX_BACKOFF_FACTOR,
  RECALL_MAX_MS,
  RECALL_MIN_GAP_MS,
  RECALL_MIN_MS,
  RECALL_RETRY_GAP_MS,
  VIDEO_SYNC_VERSION,
  activeVideoNotificationsQueryKey,
  buildChannelName,
  buildSyncKey,
} from './constants';
import { CrossTabChannel, createCrossTabChannel } from './crossTabChannel';
import { stampLastRun, tryAcquireLease } from './crossTabLease';
import { addWatchedId, reconcileWatchedFlags } from './watchedVideoIdsStore';

/**
 * Keeps the notification queue fresh over time and identical across the tabs of
 * one app.
 *
 * Three mechanisms, each answering one requirement:
 *
 *   · a jittered 5–10 minute timer, so a long-lived tab picks up newly
 *     published videos;
 *   · visibility / focus / online catch-up, so landing on a tab that has been
 *     sitting idle refetches — unless another tab already did within the gap;
 *   · a broadcast of the result, so confirming a video in one tab drops the
 *     blocking overlay in the others without a single extra request.
 *
 * The gate is shared through localStorage, so "not recalled in the last five
 * minutes" is a property of the browser, not of each tab.
 */

type VideoNotificationSyncMessage =
  | { type: 'watched'; id: number }
  | { type: 'queue'; videos: VideoNotificationDto[] };

interface UseVideoNotificationRecallOptions {
  /** Identity string (`agent:42` / `user:7`); null until it is known. */
  scope: string | null;
  /** The host's permission gate. Everything here is inert when false. */
  enabled: boolean;
  /** Stable refetch for the queue query. */
  refetch: () => Promise<{ isError?: boolean; error?: unknown }>;
}

interface UseVideoNotificationRecallResult {
  /** Call after `POST /watched` succeeds: persist, patch, tell the other tabs. */
  markWatched: (id: number) => void;
  /** Call on every successful fetch: hand the result to the other tabs. */
  publishQueue: (videos: VideoNotificationDto[]) => void;
}

export const useVideoNotificationRecall = ({
  scope,
  enabled,
  refetch,
}: UseVideoNotificationRecallOptions): UseVideoNotificationRecallResult => {
  const queryClient = useQueryClient();
  const channelRef = useRef<CrossTabChannel<VideoNotificationSyncMessage> | null>(
    null
  );
  const inFlightRef = useRef(false);
  const failureCountRef = useRef(0);
  const lastFocusRef = useRef(0);

  // Kept in a ref so a new `refetch` identity on every render does not tear
  // down and rebuild the channel, the timer and the listeners.
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  const isActive = enabled && !!scope;

  const patchWatched = useCallback(
    (id: number) => {
      if (!scope) return;
      queryClient.setQueryData<VideoNotificationDto[]>(
        activeVideoNotificationsQueryKey(scope),
        (current) =>
          // Returning `current` untouched when there is no cache entry matters:
          // a tab that has never fetched must not materialise a queue out of a
          // broadcast.
          Array.isArray(current)
            ? current.map((video) =>
                Number(video.id) === Number(id)
                  ? { ...video, isWatched: true }
                  : video
              )
            : current
      );
    },
    [queryClient, scope]
  );

  useEffect(() => {
    if (!isActive || !scope) return undefined;

    const leaseKey = buildSyncKey(scope, 'recall');
    const channel = createCrossTabChannel<VideoNotificationSyncMessage>(
      buildChannelName(scope),
      scope,
      VIDEO_SYNC_VERSION
    );
    channelRef.current = channel;

    const unsubscribe = channel.subscribe((message) => {
      if (message.type === 'watched') {
        addWatchedId(scope, message.id);
        patchWatched(message.id);
        return;
      }
      // The sender already stamped the shared clock, so adopting its payload
      // costs this tab nothing and saves it a request.
      queryClient.setQueryData(
        activeVideoNotificationsQueryKey(scope),
        reconcileWatchedFlags(scope, message.videos)
      );
    });

    /** `force`: this tab has nothing to show yet - skip the shared gap, still take the lease (one fetch for all) */
    const maybeRecall = async ({ force = false }: { force?: boolean } = {}) => {
      if (inFlightRef.current) return;

      const lease = await tryAcquireLease(leaseKey, {
        minGapMs: force ? 0 : RECALL_MIN_GAP_MS,
        leaseMs: RECALL_LEASE_MS,
        retryGapMs: RECALL_RETRY_GAP_MS,
      });
      if (!lease) return;

      inFlightRef.current = true;
      try {
        // react-query resolves `refetch` with the observer result rather than
        // rejecting, so a failure has to be read off the result.
        const result = await refetchRef.current();
        if (result?.isError) throw result.error ?? new Error('recall failed');
        failureCountRef.current = 0;
        lease.release({ at: Date.now() });
      } catch (error) {
        failureCountRef.current += 1;
        lease.release({ failed: true });
        console.warn('[video-notifications] recall failed', error);
      } finally {
        inFlightRef.current = false;
      }
    };

    let timer: number | undefined;

    const scheduleNext = () => {
      const base = RECALL_MIN_MS + Math.random() * (RECALL_MAX_MS - RECALL_MIN_MS);
      const backoff = Math.min(
        2 ** failureCountRef.current,
        RECALL_MAX_BACKOFF_FACTOR
      );
      timer = window.setTimeout(() => {
        // A hidden tab has nothing to show, and browsers throttle its timers
        // anyway. It stays correct through the channel and catches up the
        // moment it becomes visible.
        if (document.visibilityState === 'visible') void maybeRecall();
        scheduleNext();
      }, base * backoff);
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') void maybeRecall();
    };

    const onFocus = () => {
      const now = Date.now();
      if (now - lastFocusRef.current < FOCUS_THROTTLE_MS) return;
      lastFocusRef.current = now;
      void maybeRecall();
    };

    const onOnline = () => {
      void maybeRecall();
    };

    // The tab just opened: it renders from the stored queue (queueSnapshotStore) and fetches only when that is
    // older than the shared gap - or when it has nothing at all, still under the lease so a burst of new tabs
    // makes one call and the rest take the broadcast.
    const hasQueue = Array.isArray(queryClient.getQueryData(activeVideoNotificationsQueryKey(scope)));
    void maybeRecall({ force: !hasQueue });

    scheduleNext();
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', onFocus);
    window.addEventListener('online', onOnline);

    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('online', onOnline);
      unsubscribe();
      channel.close();
      channelRef.current = null;
    };
  }, [isActive, scope, patchWatched, queryClient]);

  const markWatched = useCallback(
    (id: number) => {
      if (!scope) return;
      addWatchedId(scope, id);
      patchWatched(id);
      channelRef.current?.post({ type: 'watched', id });
    },
    [patchWatched, scope]
  );

  const publishQueue = useCallback(
    (videos: VideoNotificationDto[]) => {
      if (!scope) return;
      // Counts as a recall for every tab, including the first fetch of a freshly
      // opened tab — otherwise landing on a sibling tab seconds later would
      // fetch the same queue again.
      stampLastRun(buildSyncKey(scope, 'recall'));
      channelRef.current?.post({ type: 'queue', videos });
    },
    [scope]
  );

  return { markWatched, publishQueue };
};
